import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowDown, ArrowRight, Feather, Quotes } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useConfig } from "@/hooks/useConfig";
import { BookCard } from "@/components/store/BookCard";
import { PoemPreview } from "@/components/store/PoemPreview";
import { ReadersWall } from "@/components/store/ReadersWall";
import { TrustBadges } from "@/components/store/TrustBadges";

const HERO_TEXTURE = "https://static.prod-images.emergentagent.com/jobs/2c34b422-e427-45fc-b91d-27503afbcd95/images/5d14f3b067351ec750e956f72d64bfe48db0e4cb715cb914d2e6bedcd35b3efb.jpeg";
const PORTRAIT = "https://static.prod-images.emergentagent.com/jobs/2c34b422-e427-45fc-b91d-27503afbcd95/images/05591646b57e6873039a5ceaf8898969f50976b7fea2bc0706d0f5d52b02a832.jpeg";

const THEMES = [
  { ta: "காதல்", en: "Love & longing" },
  { ta: "பிரிவு", en: "Separation" },
  { ta: "சுயமரியாதை", en: "Self-respect" },
  { ta: "மானுடம்", en: "Humanity" },
  { ta: "விடாமுயற்சி", en: "Perseverance" },
  { ta: "உறவு", en: "Relationships" },
];

const ease = [0.22, 1, 0.36, 1];

export default function Home() {
  const { config } = useConfig();
  const location = useLocation();
  const { data: books = [], isLoading } = useQuery({ queryKey: ["books"], queryFn: async () => (await api.get("/books")).data });
  const lead = books[0];
  const totalStock = lead ? (lead.physical_enabled ? lead.stock : 0) + (lead.hardcover_enabled ? lead.hardcover_stock : 0) : 0;
  const paragraphs = (config?.about || "").split("\n\n").filter(Boolean);

  useEffect(() => {
    if (location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
  }, [location.hash]);

  return (
    <div data-testid="home-page">
      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="paper-grain relative overflow-hidden border-b border-foreground/10">
        <span aria-hidden className="tamil-watermark absolute -right-6 top-6 select-none text-[9rem] font-semibold sm:text-[14rem] lg:text-[19rem]">அகம்</span>
        <div className="relative mx-auto grid max-w-[1400px] gap-16 px-5 pb-24 pt-20 sm:px-8 md:grid-cols-12 md:gap-10 md:pb-36 md:pt-28">
          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease }} className="md:col-span-7">
            <p className="eyebrow flex items-center gap-2" data-testid="hero-eyebrow">
              <Feather size={15} weight="duotone" /> {config?.store_name || "ShansoBooks"} · direct from the poet
            </p>
            <h1 className="mt-7 text-4xl font-medium leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
              Poems written in Tamil,
              <br />
              <em className="font-normal italic text-primary">felt in every language.</em>
            </h1>
            <p className="mt-3 font-display text-2xl text-foreground/45 sm:text-3xl">அகமலி கேளிர்</p>
            <p className="mt-8 max-w-xl text-base leading-[1.75] text-foreground/75 sm:text-lg">
              <span className="font-semibold text-foreground">Agamali Kaeleer</span> — {config?.author_name || "Shunmugavel"}'s debut collection.
              Eighty-six pages on love, longing, honesty and the quiet arithmetic of human relationships. Available as a paperback or a
              cloth-bound hardcover, packed by hand and shipped anywhere in India.
            </p>
            <div className="mt-11 flex flex-wrap items-center gap-5">
              <a href="#shelf" className="group inline-flex items-center gap-2.5 rounded-sm bg-primary px-8 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-200 hover:bg-[#702F2A]" data-testid="hero-browse-button">
                Read the collection <ArrowDown weight="bold" className="transition-transform duration-200 group-hover:translate-y-0.5" />
              </a>
              <a href="#poems" className="inline-flex items-center gap-2 border-b border-foreground/25 pb-1 text-sm font-semibold text-foreground/80 transition-colors hover:border-primary hover:text-primary" data-testid="hero-poems-link">
                Read a few poems <ArrowRight size={15} weight="bold" />
              </a>
              <a href="#about" className="inline-flex items-center gap-2 border-b border-foreground/25 pb-1 text-sm font-semibold text-foreground/80 transition-colors hover:border-primary hover:text-primary" data-testid="hero-about-link">
                About the poet <ArrowRight size={15} weight="bold" />
              </a>
            </div>
            <dl className="mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-foreground/10 pt-7">
              {[
                ["86", "pages of verse"],
                [lead ? `₹${lead.physical_price}` : "₹480", "paperback"],
                [lead?.hardcover_enabled ? `₹${lead.hardcover_price}` : "₹635", "hardcover"],
              ].map(([v, l]) => (
                <div key={l}>
                  <dt className="font-display text-2xl font-semibold sm:text-3xl">{v}</dt>
                  <dd className="mt-1 text-xs uppercase tracking-[0.14em] text-muted-foreground">{l}</dd>
                </div>
              ))}
            </dl>
            {lead && (
              <p className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-testid="hero-availability">
                {totalStock > 0 ? (
                  <>
                    <span className="inline-flex items-center gap-1.5 font-semibold text-accent">
                      <span className="h-2 w-2 rounded-full bg-[#3F6B3A]" /> {totalStock} {totalStock === 1 ? "copy" : "copies"} in stock
                    </span>
                    <span className="text-muted-foreground">
                      {lead.physical_enabled && `${lead.stock} paperback`}
                      {lead.physical_enabled && lead.hardcover_enabled && " · "}
                      {lead.hardcover_enabled && `${lead.hardcover_stock} hardcover`}
                    </span>
                  </>
                ) : (
                  <span className="font-semibold text-destructive">Sold out for now — a new print run is on the way</span>
                )}
              </p>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, delay: 0.15, ease }} className="relative md:col-span-5">
            <img src={HERO_TEXTURE} alt="" aria-hidden className="absolute inset-0 h-full w-full rounded-sm object-cover opacity-90" />
            <div className="relative flex h-full items-center justify-center px-8 py-14 sm:py-20">
              {lead ? (
                <Link to={`/books/${lead.slug}`} className="block w-[230px] sm:w-[280px]" data-testid="hero-cover-link">
                  <img src={lead.cover_url} alt={lead.title} className="cover-shadow lift aspect-[2/3] w-full rounded-sm object-cover" />
                </Link>
              ) : (
                <div className="cover-shadow aspect-[2/3] w-[230px] rounded-sm bg-secondary sm:w-[280px]" />
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Lineage quote strip ──────────────────────────────── */}
      <section className="bg-[#1C1917] py-16 text-[#E8DFD2] md:py-20" data-testid="lineage-section">
        <div className="mx-auto grid max-w-[1400px] gap-10 px-5 sm:px-8 md:grid-cols-12">
          <div className="md:col-span-5">
            <Quotes size={34} weight="fill" className="text-[#C98A7E]" />
            <p className="mt-5 font-display text-xl leading-relaxed sm:text-2xl">
              Raised on Bharathiyar's fire and the Thirukkural's restraint.
            </p>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-base leading-[1.9] text-[#E8DFD2]/75">
              These poems come from a lifetime of watching people closely — the things they say, the things they withhold.
              Written slowly, in the language they were dreamt in, and sold here without a middleman between the poet and the reader.
            </p>
          </div>
        </div>
      </section>

      {/* ── Themes ───────────────────────────────────────────── */}
      <section className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 md:py-24" data-testid="themes-section">
        <p className="eyebrow">What the poems hold</p>
        <div className="mt-8 grid gap-px overflow-hidden rounded-sm bg-foreground/10 sm:grid-cols-3 lg:grid-cols-6">
          {THEMES.map((t, i) => (
            <motion.div
              key={t.en}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.06, ease }}
              className="bg-background px-5 py-8 transition-colors duration-300 hover:bg-muted"
              data-testid={`theme-${t.en.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <p className="font-display text-2xl text-primary">{t.ta}</p>
              <p className="mt-2 text-xs uppercase tracking-[0.14em] text-muted-foreground">{t.en}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <PoemPreview />
      <ReadersWall />

      {/* ── Shelf ────────────────────────────────────────────── */}      <section id="shelf" className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 md:py-24" data-testid="shelf-section">
        <div className="mb-16 flex items-end justify-between gap-6 border-b border-foreground/15 pb-6">
          <div>
            <p className="eyebrow">The shelf</p>
            <h2 className="mt-2 font-display text-base font-medium sm:text-lg md:text-lg">Every title, in the order I'd hand them to you.</h2>
          </div>
          <p className="hidden text-sm text-muted-foreground sm:block">{books.length} {books.length === 1 ? "title" : "titles"} · paperback & hardcover</p>
        </div>
        {isLoading ? (
          <div className="grid gap-16 md:grid-cols-12"><div className="h-[420px] w-[300px] animate-pulse rounded-sm bg-secondary md:col-span-5" /></div>
        ) : books.length === 0 ? (
          <p className="py-12 text-base text-muted-foreground" data-testid="shelf-empty">Titles are being added to the shelf. Do check back shortly.</p>
        ) : (
          <div className="space-y-24 md:space-y-32">
            {books.map((b, i) => <BookCard key={b.book_id} book={b} index={i} />)}
          </div>
        )}
      </section>

      {/* ── About the poet ───────────────────────────────────── */}
      <section id="about" className="border-y border-foreground/10 bg-[#EFEBE0] py-20 md:py-28" data-testid="about-section">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="relative">
              <div className="absolute -left-3 -top-3 h-full w-full rounded-sm border border-primary/30" aria-hidden />
              <img
                src={PORTRAIT}
                alt={config?.author_name || "Shunmugavel"}
                className="relative aspect-square w-full rounded-sm object-cover object-center contrast-[1.05] saturate-[1.05]"
                data-testid="author-portrait"
              />
            </div>
            <p className="mt-6 font-display text-xl font-semibold" data-testid="author-name">{config?.author_name || "Shunmugavel"}</p>
            <p className="text-sm text-muted-foreground">Poet · Tamil</p>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="eyebrow">About the author</p>
            <h2 className="mt-3 font-display text-base font-medium sm:text-lg">A Tamil ear, tuned to people.</h2>
            <div className="mt-7 space-y-6 text-base leading-[1.9] text-foreground/80" data-testid="author-bio">
              {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            {lead && (
              <Link to={`/books/${lead.slug}`} className="mt-10 inline-flex items-center gap-2 rounded-sm bg-foreground px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-200 hover:bg-primary" data-testid="about-buy-link">
                Get {lead.title} <ArrowRight size={15} weight="bold" />
              </Link>
            )}
            <p className="mt-6 text-sm text-muted-foreground">
              Already a reader? <Link to="/account" className="font-semibold text-primary underline-offset-4 hover:underline" data-testid="about-account-link">Find your orders →</Link>
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8">
        <TrustBadges />
      </section>
    </div>
  );
}
